import React, { useState } from 'react';
import styled from 'styled-components';


interface ShareButtonProps {
  propertyId: string | number;
  type?: string;
  className?: string;
  iconOnly?: boolean;
  path?: string;
}

const buildShareUrl = ({
  propertyId,
  type,
  path,
}: Pick<ShareButtonProps, 'propertyId' | 'type' | 'path'>) => {
  const configuredShareDomain = process.env.REACT_APP_SHARE_DOMAIN?.trim();
  const frontendDomain =
    configuredShareDomain ||
    (typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://www.livingpp.com');
  const propertyType = type || 'residential';
  const sharePath = path || `/property/${propertyType}/${propertyId}`;
  const normalizedPath = sharePath.startsWith('/') ? sharePath : `/${sharePath}`;
  const shareUrl = new URL(`${frontendDomain.replace(/\/$/, '')}${normalizedPath}`);

  return shareUrl.toString();
};

export const ShareButton: React.FC<ShareButtonProps> = ({
  propertyId,
  type,
  className,
  iconOnly,
  path,
}) => {
  const [copied, setCopied] = useState(false);

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();

    navigator.clipboard.writeText(buildShareUrl({
      propertyId,
      type,
      path,
    })).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  return (
    <StyledButton onClick={handleShare} className={className} $iconOnly={iconOnly} title="Share Link">
      <IconWrapper>
        {copied ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3"></circle>
            <circle cx="6" cy="12" r="3"></circle>
            <circle cx="18" cy="19" r="3"></circle>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
          </svg>
        )}
      </IconWrapper>
      {copied && iconOnly && <CopiedTooltip>Copied!</CopiedTooltip>}
      {!iconOnly && (copied ? 'Link Copied!' : 'Share Link')}
    </StyledButton>
  );
};

const StyledButton = styled.button<{ $iconOnly?: boolean }>`
  position: relative; /* Needed for tooltip positioning */
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #183153;
  color: #183153;
  font-weight: 600;
  border-radius: ${({ $iconOnly }) => $iconOnly ? '50%' : '30px'};
  padding: ${({ $iconOnly }) => $iconOnly ? '6px' : '8px 20px'};
  width: ${({ $iconOnly }) => $iconOnly ? '32px' : 'auto'};
  height: ${({ $iconOnly }) => $iconOnly ? '32px' : 'auto'};
  margin-top: ${({ $iconOnly }) => $iconOnly ? '5px' : '0'};
  justify-content: center;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: #f0f4f8;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(24, 49, 83, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: ${({ $iconOnly }) => $iconOnly ? '5px' : '6px 16px'};
    width: ${({ $iconOnly }) => $iconOnly ? '28px' : 'auto'};
    height: ${({ $iconOnly }) => $iconOnly ? '28px' : 'auto'};
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 5px 14px;
  }
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CopiedTooltip = styled.div`
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #183153;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  pointer-events: none;
  opacity: 0;
  z-index: 100; /* Ensure tooltip sits above other content */
  animation: fadeInOut 2s ease-in-out forwards;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -4px;
    border-width: 4px;
    border-style: solid;
    border-color: #183153 transparent transparent transparent;
  }

  @keyframes fadeInOut {
    0% { opacity: 0; transform: translateX(-50%) translateY(5px); }
    10% { opacity: 1; transform: translateX(-50%) translateY(0); }
    80% { opacity: 1; transform: translateX(-50%) translateY(0); }
    100% { opacity: 0; transform: translateX(-50%) translateY(-5px); }
  }
`;
